/**
 * #3293 arkin TOUCH 재고 전용 엑셀 포맷.
 *
 * Inventory Status 의 평면 Excel 로는 만들 수 없는, 고객이 매일 쓰는 매트릭스 시트를 조립한다.
 * 행 = 텍스처 블록 × (번들 / 4X4 클로저 / 13X4 프론탈) × 길이, 열 = 색상 27개.
 * #3303: 수량은 on_hand 가 아니라 available_quantity(on hand − 판매주문 보류 수량) 기준이다.
 *
 * 이 파일은 프로젝트 모듈에 의존하지 않는 순수 로직만 담는다(단위 테스트 대상).
 * 실제 workbook 쓰기는 화면이 excelComplicated() + XLSX.writeFile() 로 처리한다.
 */

export const TOUCH_CLASS_GROUP = 'TOUCH';
export const TOUCH_SHEET_NAME = 'BUNDLE TOTAL';
export const TOUCH_SHEET_TITLE = 'AH BUNDLE & CLOSURE STOCK  SHEET';

const UNMAPPED_SAMPLE_LIMIT = 5;

// 색상 열. 고객 시트의 좌 14 / 우 13 순서를 그대로 쓴다.
const LEFT_COLOR_CODES = ['P1B/30', 'P4/27', 'P27/613', 'T1B/27', 'T1B/99J', 'T1B/4/27', 'T4/30', 'T1B/RED', 'T1B/350', 'TF4/27', 'TF1B/30', 'TF1B/27', 'TF4/30', 'T1B/30/27'];
const RIGHT_COLOR_CODES = ['OMB3TN/27/613', 'P4/30/27', 'P4/27/613', '27', '30', '33', '350', 'GREY', 'PURPLE', '99J', 'RED', '613', 'NATURAL'];

// 고객 시트가 줄여 쓰는 표기. 코드 마스터에는 축약형이 없어 여기서만 매핑한다.
const COLOR_LABELS = {
	'OMB3TN/27/613': '3TN/27/613',
	PURPLE: 'PURP',
	NATURAL: 'NAT',
};

export const TOUCH_LEFT_COLOR_COUNT = LEFT_COLOR_CODES.length;
export const TOUCH_COLORS = LEFT_COLOR_CODES.concat(RIGHT_COLOR_CODES).map(code => ({ code, label: COLOR_LABELS[code] || code }));

function evenLengths(from, to) {
	const lengths = [];
	for (let length = from; length <= to; length += 2) lengths.push(length);
	return lengths;
}

/**
 * 블록·행 정의.
 *
 * 텍스처를 item_class.dept_a_code 로 판정하면 안 된다. arkin 마스터에서 TC4X4BW10~18(TOUCH 4X4 BODY)의
 * dept_a_code 가 'ST' 로 잘못 등록되어 있어 BW 4X4 재고가 ST 블록으로 넘어간다. 또 13X4 STRAIGHT 는
 * TC13X4ST* 와 TF13X4ST* 가 이름까지 같은 중복 클래스라 dept_b_code 로 묶으면 합산된다.
 * 그래서 class code 접두 + 길이로만 셀을 결정한다.
 *
 * DEEP 는 번들이 12~32"(10" 없음)이고 13X4 프론탈 라인 자체가 없다.
 */
export const TOUCH_BLOCKS = [
	{
		key: 'BW',
		groups: [
			{ key: 'BUNDLE', class_prefix: 'HWTOBW', label_prefix: '', lengths: evenLengths(10, 30) },
			{ key: '4X4', class_prefix: 'TC4X4BW', label_prefix: '4X4 ', lengths: evenLengths(10, 18) },
			{ key: '13X4', class_prefix: 'TF13X4BW', label_prefix: '13X4 ', lengths: evenLengths(10, 18) },
		],
	},
	{
		key: 'ST',
		groups: [
			{ key: 'BUNDLE', class_prefix: 'HWTOST', label_prefix: '', lengths: evenLengths(10, 30) },
			{ key: '4X4', class_prefix: 'TC4X4ST', label_prefix: '4X4 ', lengths: evenLengths(10, 18) },
			{ key: '13X4', class_prefix: 'TF13X4ST', label_prefix: '13X4 ', lengths: evenLengths(10, 18) },
		],
	},
	{
		key: 'DEEP',
		groups: [
			{ key: 'BUNDLE', class_prefix: 'HWTODEEP', label_prefix: '', lengths: evenLengths(12, 32) },
			{ key: '4X4', class_prefix: 'TC4X4DP', label_prefix: '4X4 ', lengths: evenLengths(10, 18) },
		],
	},
];

function isNumeric(value) {
	return value !== null && value !== undefined && value !== '' && !isNaN(Number(value));
}

function toNumber(value) {
	return isNumeric(value) ? Number(value) : 0;
}

function round2(value) {
	return Math.round((value + Number.EPSILON) * 100) / 100;
}

/**
 * 평면 raw data 행을 고정 셀에 정확히 한 번씩 배정한다.
 * 같은 셀에 여러 item 행이 오면 합산하고, 배정되지 않은 행은 전부 미매핑으로 남긴다.
 */
export function buildTouchStockMatrix(rows, options) {
	const include_value = !!(options && options.include_value);

	const color_index = new Map();
	TOUCH_COLORS.forEach((color, index) => color_index.set(color.code, index));

	const class_index = new Map();
	TOUCH_BLOCKS.forEach((block, block_index) => block.groups.forEach((group, group_index) => group.lengths.forEach((length, row_index) => class_index.set(group.class_prefix + length, { block_index, group_index, row_index }))));

	const blocks = TOUCH_BLOCKS.map(block => ({
		key: block.key,
		groups: block.groups.map(group => ({
			key: group.key,
			rows: group.lengths.map(length => ({
				length,
				label: group.label_prefix + length,
				cells: TOUCH_COLORS.map(() => ({ quantity: 0, value: 0 })),
			})),
		})),
		color_quantities: TOUCH_COLORS.map(() => 0),
		color_values: TOUCH_COLORS.map(() => 0),
		quantity: 0,
		value: 0,
	}));

	const unmapped = { quantity: 0, value: 0, row_count: 0, samples: [] };
	let source_quantity = 0;
	let source_value = 0;
	const zero_value = { rows: 0, quantity: 0 };

	(rows || []).forEach(row => {
		// #3303 수량 원천은 available_quantity 뿐이다. on_hand 로 대체하지 않는다.
		const quantity = toNumber(row.available_quantity);
		// 금액도 같은 수량 기준이어야 한다. API 의 item_total_value 는 cost × on_hand 라 쓰지 않는다.
		const value = include_value ? quantity * toNumber(row.cost) : 0;
		source_quantity += quantity;
		source_value += value;

		const class_code = String(row.class_code === null || row.class_code === undefined ? '' : row.class_code).trim();
		const color_code = String(row.color_code === null || row.color_code === undefined ? '' : row.color_code).trim();
		const position = class_index.get(class_code);
		const column = color_index.get(color_code);

		if (!position || column === undefined) {
			unmapped.quantity += quantity;
			unmapped.value += value;
			if (quantity !== 0) {
				unmapped.row_count++;
				if (unmapped.samples.length < UNMAPPED_SAMPLE_LIMIT) unmapped.samples.push({ class_code, color_code, quantity });
			}
			return;
		}

		const block = blocks[position.block_index];
		const cell = block.groups[position.group_index].rows[position.row_index].cells[column];
		cell.quantity += quantity;
		cell.value += value;
		block.color_quantities[column] += quantity;
		block.color_values[column] += value;
		block.quantity += quantity;
		block.value += value;

		// raw-data API 는 cost 가 NULL 이어도 cost=0, item_total_value=0 으로 내려주므로
		// (ItemMasterMapper.xml 의 IFNULL) 누락 여부를 필드로 구분할 수 없다.
		// 시트에 실린 행 중 수량이 있는데 금액이 0이면 TTL PRICE 가 그만큼 축소된다는 사실만 남긴다.
		if (include_value && quantity !== 0 && value === 0) {
			zero_value.rows++;
			zero_value.quantity += quantity;
		}
	});

	const total_quantity = blocks.reduce((sum, block) => sum + block.quantity, 0);
	const total_value = blocks.reduce((sum, block) => sum + block.value, 0);

	return {
		blocks,
		colors: TOUCH_COLORS,
		include_value,
		unmapped,
		zero_value,
		source_quantity,
		source_value,
		mapped_quantity: total_quantity,
		mapped_value: total_value,
		total_quantity,
		total_value,
	};
}

/** 미매핑 재고가 있으면 NOTE·토스트에 쓸 안내 문구를 만든다. 없으면 빈 문자열. */
export function touchStockUnmappedMessage(matrix) {
	const unmapped = matrix.unmapped;
	if (!unmapped || unmapped.row_count === 0) return '';
	const samples = unmapped.samples.map(sample => `${sample.class_code}/${sample.color_code}(${sample.quantity})`).join(', ');
	const more = unmapped.row_count > unmapped.samples.length ? ' ...' : '';
	return `NOT ON THIS SHEET: ${unmapped.row_count} item(s), qty ${unmapped.quantity} - ${samples}${more}`;
}

/** 금액 0으로 실린 재고가 있으면 TTL PRICE 가 축소되었음을 알린다. 없으면 빈 문자열. */
export function touchStockZeroValueMessage(matrix) {
	const zero_value = matrix.zero_value;
	if (!matrix.include_value || !zero_value || zero_value.rows === 0) return '';
	return `NO COST: ${zero_value.rows} item(s), qty ${zero_value.quantity} counted at $0 - TTL PRICE is lower by that much.`;
}

/** NOTE 칸에 넣을 경고 문구. 없으면 빈 문자열. */
export function touchStockNoteText(matrix) {
	return [touchStockUnmappedMessage(matrix), touchStockZeroValueMessage(matrix)].filter(Boolean).join('  |  ');
}

// ---------------------------------------------------------------------------
// 시트 레이아웃. 고객 첨부 파일과 같은 A~AF 32열(A·B 비움, C부터 시작)을 쓴다.
// ---------------------------------------------------------------------------

const COLUMN_COUNT = 32;

// 고객 파일의 열 너비(문자 단위). null 은 기본값.
const COLUMN_WIDTHS = [5.86, null, 26.57, 20.14, 23.43, null, null, 23.86, 22.14, 23.43, 26.29, null, 19.29, 23.43, null, 21.43, 27.86, 28.71, 32.0, 25.43, 25.29, 17.71, null, 19.14, 16.86, 18.71, null, 16.86, 23.43, 20.14, 23.43, 27.29];

// 고객 파일의 행 높이. xlsx-js-style 은 기본 96DPI 에서 hpx 값을 그대로 pt 로 기록하므로 pt 값을 넣는다.
// 첫 블록 헤더와 블록 사이 빈 행은 고객 파일의 값이 서로 달라 위치별로 그대로 맞춘다.
const ROW_HEIGHT = { title: 84.75, form: 61.5, first_block_header: 94.5, block_header: 67.5, data: 50.1, group_spacer: 30, block_spacer: 24.75, subtotal: 50.1, note: 50.1 };

const FILL_WHITE = 'FFFFFF';
const FILL_BLOCK_HEADER = 'FAC090';
const FILL_4X4 = 'FDEADA';
const FILL_13X4 = 'FCD5B5';
const GROUP_FILLS = { BUNDLE: null, '4X4': FILL_4X4, '13X4': FILL_13X4 };

const FONT_TITLE = { sz: 36, bold: true };
const FONT_FORM = { sz: 36, bold: true };
const FONT_COLOR_HEADER = { sz: 26, bold: true };
const FONT_ROW_LABEL = { sz: 36, bold: true };
const FONT_DATA = { sz: 40 };
const FONT_SUBTOTAL = { sz: 30 };
const FONT_NOTE = { sz: 36, bold: true };
const FONT_NOTE_TEXT = { sz: 14 };

const QUANTITY_FORMAT = '#,##0';
const VALUE_FORMAT = '"$"#,##0.00';
const DATE_FORMAT = 'mm/dd/yy';

const THIN = { style: 'thin' };
const MEDIUM = { style: 'medium' };
const DOUBLE = { style: 'double' };

function border(top, right, bottom, left) {
	const result = {};
	if (top) result.top = top;
	if (right) result.right = right;
	if (bottom) result.bottom = bottom;
	if (left) result.left = left;
	return result;
}

const CENTER = { horizontal: 'center', vertical: 'center' };

function style(font, fill, cell_border, extra) {
	const result = { font: font, alignment: CENTER, border: cell_border };
	if (fill) result.fill = { fgColor: { rgb: fill } };
	if (extra) Object.assign(result, extra);
	return result;
}

function textCell(value, cell_style, span) {
	const cell = { t: 's', v: value, s: cell_style };
	if (span) Object.assign(cell, span);
	return cell;
}

function numberCell(value, cell_style, span) {
	const cell = { t: 'n', v: value, s: cell_style };
	if (span) Object.assign(cell, span);
	return cell;
}

/** 로컬 달력 기준 Excel 날짜 일련번호. 타임존 때문에 하루가 밀리지 않게 UTC 로 계산한다. */
export function excelDateSerial(date) {
	return Math.floor((Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()) - Date.UTC(1899, 11, 30)) / 86400000);
}

function emptyRow(height) {
	const row = [];
	for (let index = 0; index < COLUMN_COUNT; index++) {
		row.push(textCell('', undefined, index === 0 && height ? { height: { hpx: height } } : null));
	}
	return row;
}

function leadingCells(height) {
	return [textCell('', undefined, height ? { height: { hpx: height } } : null), textCell('', undefined, null)];
}

/**
 * excelComplicated() 가 그대로 받는 2차원 셀 배열을 만든다.
 * 배열 인덱스 = 열 위치(A=0 ... AF=31). 단, 위 행의 rowspan 이 차지한 열에는 셀을 넣지 않는다.
 */
export function buildTouchStockSheetCells(matrix, options) {
	const report_date = (options && options.report_date) || new Date();
	const include_value = matrix.include_value;
	const data = [];

	// 1행: 제목
	const title_row = leadingCells(ROW_HEIGHT.title);
	title_row.push(textCell(TOUCH_SHEET_TITLE, style(FONT_TITLE, FILL_WHITE, border(MEDIUM, MEDIUM, MEDIUM, MEDIUM)), { cs: 30 }));
	data.push(title_row);

	// 2행: ACCOUNT / STORE NAME / SALES CODE 는 고객이 손으로 채우는 빈칸이고 DATE 만 채운다.
	const form_style = style(FONT_FORM, FILL_WHITE, border(MEDIUM, THIN, DOUBLE, THIN));
	const form_row = leadingCells(ROW_HEIGHT.form);
	form_row.push(textCell('ACCOUNT', form_style, { cs: 2 }));
	form_row.push(textCell('', form_style, { cs: 5 }));
	form_row.push(textCell('STORE NAME', form_style, { cs: 2 }));
	form_row.push(textCell('', form_style, { cs: 9 }));
	form_row.push(textCell('SALES CODE', form_style, { cs: 3 }));
	form_row.push(textCell('', form_style, { cs: 4 }));
	form_row.push(textCell('DATE', form_style, { cs: 2 }));
	form_row.push(numberCell(excelDateSerial(report_date), style(FONT_FORM, FILL_WHITE, border(MEDIUM, THIN, DOUBLE, THIN), { numFmt: DATE_FORMAT }), { cs: 3 }));
	data.push(form_row);

	matrix.blocks.forEach((block, block_index) => {
		if (block_index > 0) data.push(emptyRow(ROW_HEIGHT.block_spacer));

		// 블록 헤더: 좌·중·우 라벨 열에 블록 키를 반복하고 색상 코드를 늘어놓는다.
		const header_style = style(FONT_COLOR_HEADER, FILL_BLOCK_HEADER, border(THIN, THIN, THIN, THIN));
		const label_header_style = style(FONT_ROW_LABEL, FILL_BLOCK_HEADER, border(THIN, THIN, THIN, THIN));
		const header_row = leadingCells(block_index === 0 ? ROW_HEIGHT.first_block_header : ROW_HEIGHT.block_header);
		header_row.push(textCell(block.key, label_header_style, COLUMN_WIDTHS[2] ? { width: { wch: COLUMN_WIDTHS[2] } } : null));
		matrix.colors.forEach((color, color_index) => {
			if (color_index === TOUCH_LEFT_COLOR_COUNT) header_row.push(textCell(block.key, label_header_style, null));
			header_row.push(textCell(color.label, header_style, null));
		});
		header_row.push(textCell(block.key, label_header_style, null));
		// 열 너비는 배열 인덱스 = 열 위치가 성립하는 이 행에서만 지정한다.
		header_row.forEach((cell, index) => {
			if (COLUMN_WIDTHS[index]) cell.width = { wch: COLUMN_WIDTHS[index] };
		});
		data.push(header_row);

		block.groups.forEach((group, group_index) => {
			// 고객 시트는 번들과 4X4 사이에만 빈 행을 둔다.
			if (group_index === 1) data.push(emptyRow(ROW_HEIGHT.group_spacer));

			const fill = GROUP_FILLS[group.key];
			const label_style = style(FONT_ROW_LABEL, fill, border(THIN, THIN, THIN, THIN));
			const data_style = style(FONT_DATA, fill, border(THIN, THIN, THIN, THIN), { numFmt: QUANTITY_FORMAT });

			group.rows.forEach(row => {
				const sheet_row = leadingCells(ROW_HEIGHT.data);
				sheet_row.push(textCell(row.label, label_style, null));
				row.cells.forEach((cell, color_index) => {
					if (color_index === TOUCH_LEFT_COLOR_COUNT) sheet_row.push(textCell(row.label, label_style, null));
					sheet_row.push(numberCell(cell.quantity, data_style, null));
				});
				sheet_row.push(textCell(row.label, label_style, null));
				data.push(sheet_row);
			});
		});

		// 소계: 수량 행(+ 금액 행). STTL 라벨과 블록 총수량은 두 행에 걸친다.
		const subtotal_border = border(MEDIUM, THIN, include_value ? THIN : MEDIUM, THIN);
		const subtotal_label_style = style(FONT_ROW_LABEL, FILL_WHITE, subtotal_border);
		const subtotal_style = style(FONT_SUBTOTAL, FILL_WHITE, subtotal_border, { numFmt: QUANTITY_FORMAT });
		const span = include_value ? { rs: 2 } : null;

		const subtotal_row = leadingCells(ROW_HEIGHT.subtotal);
		subtotal_row.push(textCell('STTL', subtotal_label_style, span));
		block.color_quantities.forEach((quantity, color_index) => {
			if (color_index === TOUCH_LEFT_COLOR_COUNT) subtotal_row.push(textCell('', subtotal_label_style, null));
			subtotal_row.push(numberCell(quantity, subtotal_style, null));
		});
		subtotal_row.push(numberCell(block.quantity, style(FONT_ROW_LABEL, FILL_WHITE, subtotal_border, { numFmt: QUANTITY_FORMAT }), span));
		data.push(subtotal_row);

		if (include_value) {
			const value_border = border(THIN, THIN, MEDIUM, THIN);
			const value_style = style(FONT_SUBTOTAL, FILL_WHITE, value_border, { numFmt: VALUE_FORMAT });
			// C 열(STTL)과 AF 열(블록 총수량)은 위 행의 rowspan 이 차지하므로 셀을 넣지 않는다.
			const value_row = leadingCells(ROW_HEIGHT.subtotal);
			block.color_values.forEach((value, color_index) => {
				if (color_index === TOUCH_LEFT_COLOR_COUNT) value_row.push(textCell('', style(FONT_SUBTOTAL, FILL_WHITE, value_border), null));
				value_row.push(numberCell(round2(value), value_style, null));
			});
			data.push(value_row);
		}
	});

	// 마지막 행: NOTE / 총수량 / TTL PRICE
	const note_border = border(MEDIUM, MEDIUM, MEDIUM, MEDIUM);
	const note_style = style(FONT_NOTE, FILL_WHITE, note_border);
	const note_row = leadingCells(ROW_HEIGHT.note);
	note_row.push(textCell('NOTE', note_style, null));
	note_row.push(textCell(touchStockNoteText(matrix), style(FONT_NOTE_TEXT, FILL_WHITE, note_border, { alignment: { horizontal: 'left', vertical: 'center' } }), { cs: 13 }));
	for (let index = 0; index < 8; index++) note_row.push(textCell('', note_style, null));
	note_row.push(numberCell(matrix.total_quantity, style(FONT_NOTE, FILL_WHITE, note_border, { numFmt: QUANTITY_FORMAT }), { cs: 2 }));
	if (include_value) {
		note_row.push(textCell('TTL  PRICE', note_style, { cs: 3 }));
		note_row.push(numberCell(round2(matrix.total_value), style(FONT_NOTE, FILL_WHITE, note_border, { numFmt: VALUE_FORMAT }), { cs: 3 }));
	} else {
		note_row.push(textCell('', note_style, { cs: 6 }));
	}
	data.push(note_row);

	return data;
}

/** touchStockSheet_YYYYMMDD.xlsx */
export function touchStockFileName(report_date) {
	const date = report_date || new Date();
	const month = String(date.getMonth() + 1).padStart(2, '0');
	const day = String(date.getDate()).padStart(2, '0');
	return `touchStockSheet_${date.getFullYear()}${month}${day}.xlsx`;
}
